import React, { useEffect, useMemo, useState } from 'react';
import { keyframes } from '@mui/system';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import { Share2, RotateCcw, ArrowRight } from 'lucide-react';
import { getLocalizedPath } from 'helpers/getLocale';
import { getProfessionSlug } from 'utils/seo';
import { Card, Body, Muted, PrimaryButton, SecondaryButton, Pill } from './ui';
import { skillUrl } from './KpiPanel';
import { motion, popIn, REDUCED, useCountUp, prefersReducedMotion } from './motion';
import { L, t } from '../strings';

const Hero = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  display: 'grid',
  gridTemplateColumns: '150px 1fr',
  gap: 28,
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr', gap: 16 }
}));

const RingBox = styled('div')(() => ({
  position: 'relative',
  width: 150,
  height: 150,
  '& svg': { display: 'block', transform: 'rotate(-90deg)' },
  '& > div': {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Unbounded',
    fontSize: 34,
    fontWeight: 600,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums'
  }
}));

const scoreColor = (v) => (v >= 60 ? '#79c912' : v >= 35 ? '#d5921b' : '#b21625');

const R = 66;
const C = 2 * Math.PI * R;

const ScoreRing = ({ value }) => {
  const shown = useCountUp(value, 1400, 0);
  return (
    <RingBox>
      <svg width="150" height="150" viewBox="0 0 150 150" aria-hidden="true">
        <circle cx="75" cy="75" r={R} fill="none" stroke="#ebedee" strokeWidth="14" />
        <circle
          cx="75"
          cy="75"
          r={R}
          fill="none"
          stroke={scoreColor(shown)}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={C * (1 - shown / 100)}
        />
      </svg>
      <div>
        {Math.round(shown)}
        <Muted style={{ fontSize: 12, marginTop: 4 }}>/100</Muted>
      </div>
    </RingBox>
  );
};

const fall = keyframes`
  0% { transform: translate3d(0, -20px, 0) rotate(0deg); opacity: 1; }
  100% { transform: translate3d(var(--dx), 420px, 0) rotate(var(--rot)); opacity: 0; }
`;

const ConfettiLayer = styled('div')(() => ({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
  '& i': {
    position: 'absolute',
    top: 0,
    width: 8,
    height: 12,
    borderRadius: 2,
    opacity: 0,
    animation: `${fall} 2.4s cubic-bezier(.2,.6,.4,1) forwards`
  },
  [REDUCED]: { display: 'none' }
}));

const CONFETTI = ['#B9F833', '#79c912', '#f7d53e', '#30b0c7', '#9c27b0', '#e25510'];

const Confetti = () => {
  const pieces = useMemo(
    () =>
      Array.from({ length: 42 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        dx: (Math.random() - 0.5) * 160,
        rot: 360 + Math.random() * 540,
        color: CONFETTI[i % CONFETTI.length],
        round: i % 3 === 0
      })),
    []
  );
  return (
    <ConfettiLayer aria-hidden="true">
      {pieces.map((p, i) => (
        <i
          key={i}
          style={{
            left: `${p.left}%`,
            background: p.color,
            borderRadius: p.round ? '50%' : 2,
            width: p.round ? 8 : 7,
            height: p.round ? 8 : 12,
            animationDelay: `${p.delay}s`,
            '--dx': `${p.dx}px`,
            '--rot': `${p.rot}deg`
          }}
        />
      ))}
    </ConfettiLayer>
  );
};

const Grid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 20,
  [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr' }
}));

const Bar = styled('div')(({ theme }) => ({
  height: 8,
  borderRadius: 100,
  backgroundColor: theme.palette.grey[100],
  overflow: 'hidden',
  marginTop: 5,
  marginBottom: 12
}));
const Fill = styled('div')(({ value, color, delay = 0 }) => ({
  height: '100%',
  width: `${value}%`,
  backgroundColor: color || '#79c912',
  borderRadius: 100,
  transition: `width 900ms cubic-bezier(.2,.8,.2,1) ${delay}ms`
}));

const TraitPill = styled(Pill)(({ delay }) => motion(`${popIn} 320ms ease-out ${delay}ms both`));
const LabelRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  fontFamily: 'Rubik-Regular',
  fontSize: 14
}));

const List = styled('ul')(() => ({
  margin: 0,
  paddingLeft: 0,
  listStyle: 'none',
  '& li': {
    position: 'relative',
    paddingLeft: 26,
    marginBottom: 10,
    fontFamily: 'Rubik-Regular',
    fontSize: 16,
    lineHeight: '24px'
  },
  '& li::before': {
    content: '"✓"',
    position: 'absolute',
    left: 0,
    color: '#79c912',
    fontWeight: 700
  }
}));

const SkillLink = styled(Link)(({ theme }) => ({
  color: theme.palette.textPrimary.main,
  textDecoration: 'underline',
  textDecorationColor: theme.palette.primary.main,
  textUnderlineOffset: 3,
  '&:hover': { color: '#4f9a04' }
}));

const Actions = styled('div')(() => ({ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }));

const kpiColor = scoreColor;

export default function Result({ pack, summary, kpis, onRestart }) {
  const [copied, setCopied] = useState(false);
  // Bars start empty and grow in once the page has painted.
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setGrown(true), 250);
    return () => clearTimeout(id);
  }, []);
  const celebrate = useMemo(() => summary.score100 >= 60 && !prefersReducedMotion(), [summary]);
  const occupationUrl = getLocalizedPath(
    `/professions/${getProfessionSlug({ name: L(pack.occupationName) })}/${pack.occupationId}`
  );
  const traitLabels = t('traits');

  const share = async () => {
    const url = window.location.href.split('?')[0];
    const text = t('shareText', {
      s: summary.score100,
      job: L(pack.occupationName),
      site: window.location.hostname
    });
    try {
      if (navigator.share) {
        await navigator.share({ text, url });
        return;
      }
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* user cancelled or clipboard blocked */
    }
  };

  return (
    <div>
      <Hero>
        {celebrate && <Confetti />}
        <ScoreRing value={summary.score100} />
        <div>
          <Muted style={{ marginBottom: 4 }}>
            {t('resultTitle')} · {L(pack.occupationName)}
          </Muted>
          <Typography variant="h2" style={{ fontSize: 26, lineHeight: '34px', marginBottom: 8 }}>
            {L(summary.band.title)}
          </Typography>
          <Body>{L(summary.band.text)}</Body>
          <Actions>
            <PrimaryButton onClick={share} startIcon={<Share2 size={16} />}>
              {copied ? t('copied') : t('share')}
            </PrimaryButton>
            <SecondaryButton onClick={onRestart} startIcon={<RotateCcw size={16} />}>
              {t('playAgain')}
            </SecondaryButton>
          </Actions>
        </div>
      </Hero>

      <Grid>
        <Card style={{ animationDelay: '120ms' }}>
          <Typography variant="h3" style={{ fontSize: 18, marginBottom: 14 }}>
            {t('howItEnded')}
          </Typography>
          {[
            ...pack.kpis.map((k) => ({ key: k.key, label: L(k.label) })),
            { key: 'trust', label: t('trust') },
            { key: 'energy', label: t('energy') }
          ].map((k, i) => (
            <div key={k.key}>
              <LabelRow>
                <span>{k.label}</span>
                <span>{Math.round(kpis[k.key])}</span>
              </LabelRow>
              <Bar>
                <Fill
                  value={grown ? kpis[k.key] : 0}
                  color={kpiColor(kpis[k.key])}
                  delay={i * 80}
                />
              </Bar>
            </div>
          ))}
        </Card>
        <Card style={{ animationDelay: '200ms' }}>
          <Typography variant="h3" style={{ fontSize: 18, marginBottom: 14 }}>
            {t('skillProfile')}
          </Typography>
          {summary.competencies.map((c, i) => (
            <div key={c.key}>
              <LabelRow>
                <span>
                  {c.skill ? (
                    <SkillLink to={skillUrl(c.skill)} title={c.skill.name}>
                      {L(c.label)}
                    </SkillLink>
                  ) : (
                    L(c.label)
                  )}
                </span>
                <span>{c.value == null ? '–' : Math.round(c.value * 100)}</span>
              </LabelRow>
              <Bar>
                <Fill value={grown && c.value != null ? c.value * 100 : 0} delay={200 + i * 80} />
              </Bar>
            </div>
          ))}
        </Card>
      </Grid>

      <Card style={{ animationDelay: '280ms' }}>
        <Typography variant="h3" style={{ fontSize: 18, marginBottom: 14 }}>
          {t('reality')}
        </Typography>
        <List>
          {pack.verdict.reality.map((r, i) => (
            <li key={i}>{L(r)}</li>
          ))}
        </List>
      </Card>

      <Card style={{ animationDelay: '360ms' }}>
        <Typography variant="h3" style={{ fontSize: 18, marginBottom: 6 }}>
          {t('fit')}
        </Typography>
        <Muted style={{ marginBottom: 12 }}>{t('fitHint')}</Muted>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
          {summary.traits.map((tr, i) => (
            <TraitPill
              key={tr.key}
              delay={500 + i * 70}
              color={tr.value >= 0.6 ? '#79c912' : '#d6d6d6'}>
              {traitLabels[tr.key]} · {Math.round(tr.value * 100)}
            </TraitPill>
          ))}
        </div>
        <Actions>
          <PrimaryButton
            component={Link}
            to={getLocalizedPath('/assessment')}
            endIcon={<ArrowRight size={16} />}>
            {t('takeAssessment')}
          </PrimaryButton>
          <SecondaryButton component={Link} to={occupationUrl}>
            {t('aboutOccupation')}
          </SecondaryButton>
          <SecondaryButton component={Link} to={getLocalizedPath('/simulator')}>
            {t('tryAnother')}
          </SecondaryButton>
        </Actions>
      </Card>
    </div>
  );
}
